import React, { useState, useEffect } from "react"
import { Link } from "@inertiajs/react"

/**
 * The listings CTA band. A visitor on a listings page is in buying mode, so
 * selling is framed as the way to fund a purchase rather than as an errand.
 *
 * The three layout states (lockup / stack / mobile) key off the band's own
 * width, not the viewport: the band sits inside the listings column, which
 * gives up the filter column's width the moment that column moves alongside it
 * at `md`. A viewport breakpoint would therefore drop the one-row lockup into a
 * column that cannot hold it. See `.sell-band-*` in resources/css/app.css.
 *
 * The headline is `white-space: nowrap` in the lockup, so it has a hard length
 * ceiling: changing the copy means rechecking the state thresholds.
 *
 * The band carries no dismiss control: at this size it does not interrupt a
 * scroll, and an X in the corner is the one piece of furniture that makes an
 * in-page band read as a third-party ad.
 *
 * The backdrop is sized for retina, which is what the previous asset was not:
 * a 1180px band on a DPR-2 screen needs 2360 device pixels of image, and the
 * old 1192px-wide file was being upscaled 1.98x and read as pixelated. The
 * candidates below are cut from the lossless 1920px original, so `sizes` has
 * to keep describing the band's width or the browser picks the wrong one.
 */
const CTABannerV2 = ({ scroll = false, eager = false }) => {
    const [isVisible, setIsVisible] = useState(scroll)

    useEffect(() => {
        if (scroll) return
        const handleScroll = () => {
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop

            // Show banner after scrolling 100px
            setIsVisible(scrollTop > 100)
        }

        window.addEventListener("scroll", handleScroll)

        // Cleanup
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    if (!isVisible) {
        return null
    }

    return (
        <div className="sell-band-shell">
            <div className="sell-band">
                <img
                    src="/images/static/sell-band-backdrop-1920.webp"
                    srcSet="/images/static/sell-band-backdrop-960.webp 960w, /images/static/sell-band-backdrop-1920.webp 1920w"
                    sizes="100vw"
                    alt=""
                    aria-hidden="true"
                    loading={eager ? "eager" : "lazy"}
                    fetchpriority={eager ? "high" : "auto"}
                    className="sell-band-image"
                />
                <span aria-hidden="true" className="sell-band-wash" />

                <div className="sell-band-content">
                    <p className="sell-band-headline">
                        Need cash to fund purchases?
                    </p>

                    <div className="sell-band-pills">
                        <Link
                            href="/sell-my-gun"
                            className="sell-band-pill sell-band-pill-primary"
                        >
                            Sell a gun
                        </Link>
                        <Link
                            href="/gun-consignment"
                            className="sell-band-pill sell-band-pill-secondary"
                        >
                            Consign a gun
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CTABannerV2
